// Symbol lookup: backend ProtectionDevice.symbol_id + ratingA → SVG URL.
// Mirrors backend rendering/symbol_index.py: generic_aliases + mcb_symbol_by_rating_a.
// Returns null for unknown/missing symbols — caller must use rectangle fallback.

// Imported as raw SVG and embedded as self-contained data: URIs (see svgToDataUri)
// so the symbols render in print output — Chromium does not paint <image> that
// references an external SVG file when printing.
import mcb10Raw from './assets/mcb_1p_10a.svg?raw';
import mcb16Raw from './assets/mcb_1p_16a.svg?raw';
import mcb20Raw from './assets/mcb_1p_20a.svg?raw';
import mcb25Raw from './assets/mcb_1p_25a.svg?raw';
import mcb32Raw from './assets/mcb_1p_32a.svg?raw';
import rcdRaw from './assets/rcd_2p_40a_30ma.svg?raw';
import incomingBreakerRaw from './assets/incoming_breaker_2p_40a.svg?raw';

// Base64 data URI (not ;utf8 + percent-encoding, which renders on screen but is
// rejected by Chromium's print path). Self-contained so <image> prints.
const svgToDataUri = (svg: string): string => {
  const bytes = new TextEncoder().encode(svg);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:image/svg+xml;base64,${btoa(bin)}`;
};

const mcb10Url = svgToDataUri(mcb10Raw);
const mcb16Url = svgToDataUri(mcb16Raw);
const mcb20Url = svgToDataUri(mcb20Raw);
const mcb25Url = svgToDataUri(mcb25Raw);
const mcb32Url = svgToDataUri(mcb32Raw);
const rcdUrl = svgToDataUri(rcdRaw);
const incomingBreakerUrl = svgToDataUri(incomingBreakerRaw);

// Mirrors backend generic_aliases in symbol_index.py.
const GENERIC_ALIASES: Record<string, string> = {
  symbol_incoming_breaker: 'incoming_breaker_2p_40a',
  incoming_breaker:        'incoming_breaker_2p_40a',
  symbol_rcd:              'rcd_2p_40a_30ma',
  rcd:                     'rcd_2p_40a_30ma',
};

// Mirrors backend mcb_symbol_by_rating_a in symbol_index.py.
const MCB_BY_RATING: Partial<Record<number, string>> = {
  10: 'mcb_1p_10a',
  16: 'mcb_1p_16a',
  20: 'mcb_1p_20a',
  25: 'mcb_1p_25a',
  32: 'mcb_1p_32a',
};

// Catalog canonical id → SVG URL. Keys mirror backend symbol_render_catalog.
const SVG_ASSETS: Record<string, string> = {
  incoming_breaker_2p_40a: incomingBreakerUrl,
  rcd_2p_40a_30ma:         rcdUrl,
  mcb_1p_10a:              mcb10Url,
  mcb_1p_16a:              mcb16Url,
  mcb_1p_20a:              mcb20Url,
  mcb_1p_25a:              mcb25Url,
  mcb_1p_32a:              mcb32Url,
};

export function resolvePanelSymbol(symbolId: string, ratingA?: number): string | null {
  let canonicalId: string | null;

  if (symbolId === 'symbol_mcb' && ratingA != null) {
    canonicalId = MCB_BY_RATING[ratingA] ?? null;
  } else {
    canonicalId = GENERIC_ALIASES[symbolId] ?? null;
  }

  if (!canonicalId) return null;
  return SVG_ASSETS[canonicalId] ?? null;
}
